import React, { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Plus, Calendar, Users, MapPin, FolderKanban, Building2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import MultiSelectUsers from '@/components/MultiSelectUsers';
import FileGalleryFullScreen from '@/components/FileGalleryFullScreenNew';
import { formatErrorMessage } from '@/lib/errorHandler';
import { DndContext, DragOverlay, closestCorners, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const ELEGANT_GOLD = '#C9A961';

// Sortable Task Card Component - Redesigned
const SortableTaskCard = ({ task, getProjectName, getUserName, getPriorityBadge, onClick }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: task.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  // Set default values for backward compatibility
  const locationType = task.location_type || 'field';
  const isProjectTask = task.is_project_task !== undefined ? task.is_project_task : true;

  // Get deadline urgency
  const getDeadlineStatus = () => {
    if (!task.due_date) return null;
    
    const deadline = new Date(task.due_date);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    deadline.setHours(0, 0, 0, 0);
    
    const diffTime = deadline - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) {
      return { text: 'Overdue', color: 'text-red-400', bgColor: 'bg-red-500/20', borderColor: 'border-red-500/50' };
    } else if (diffDays === 0) {
      return { text: 'Due Today', color: 'text-[#C9A961]', bgColor: 'bg-[#C9A961]/20', borderColor: 'border-[#C9A961]/50' };
    } else if (diffDays === 1) {
      return { text: 'Due Tomorrow', color: 'text-[#C9A961]', bgColor: 'bg-[#C9A961]/10', borderColor: 'border-[#C9A961]/30' };
    }
    return null;
  };

  const deadlineStatus = getDeadlineStatus();

  return (
    <Card
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      onClick={onClick}
      className="bg-black border-[#C9A961]/30 hover:border-[#C9A961] hover:shadow-lg hover:shadow-[#C9A961]/20 transition-all duration-200 cursor-pointer group mb-3"
    >
      <CardHeader className="p-4 pb-3 space-y-3">
        {/* Type and Priority Row */}
        <div className="flex items-center justify-between gap-2">
          <Badge className={`${locationType === 'field' ? 'bg-[#C9A961]/20 text-[#C9A961]' : 'bg-gray-500/20 text-gray-400'} border-[#C9A961]/50 border px-2 py-1`}>
            {locationType === 'field' ? (
              <><Building2 className="h-3 w-3 mr-1" /> Field</>
            ) : (
              <><Building2 className="h-3 w-3 mr-1" /> Office</>
            )}
          </Badge>
          {getPriorityBadge(task.priority)}
        </div>

        {/* Title */}
        <CardTitle className="text-lg text-white group-hover:text-[#C9A961] transition-colors font-bold leading-tight">
          {task.title}
        </CardTitle>

        {/* Deadline Urgency */}
        {deadlineStatus && (
          <Badge className={`${deadlineStatus.bgColor} ${deadlineStatus.color} ${deadlineStatus.borderColor} border w-fit px-2 py-1 text-xs font-semibold`}>
            <Calendar className="h-3 w-3 mr-1" />
            {deadlineStatus.text}
          </Badge>
        )}
      </CardHeader>

      <CardContent className="p-4 pt-0 space-y-3">
        {/* Description */}
        {task.description && (
          <p className="text-gray-400 text-sm line-clamp-2 leading-relaxed">{task.description}</p>
        )}
        
        {/* Metadata */}
        <div className="space-y-2 text-sm border-t border-[#C9A961]/20 pt-3">
          {/* Project/Department */}
          {isProjectTask && task.project_id ? (
            <div className="flex items-center gap-2 text-[#C9A961]">
              <FolderKanban className="h-4 w-4 flex-shrink-0" />
              <span className="truncate font-medium">{getProjectName(task.project_id)}</span>
            </div>
          ) : task.department ? (
            <div className="flex items-center gap-2 text-gray-400">
              <Building2 className="h-4 w-4 flex-shrink-0" />
              <span className="truncate">{task.department}</span>
            </div>
          ) : (
            <div className="flex items-center gap-2 text-gray-500">
              <Building2 className="h-4 w-4 flex-shrink-0" />
              <span className="text-xs italic">Standalone Task</span>
            </div>
          )}
          
          {/* Address */}
          {task.address && (
            <div className="flex items-center gap-2 text-gray-400">
              <MapPin className="h-4 w-4 flex-shrink-0" />
              <span className="truncate">{task.address}</span>
            </div>
          )}
          
          {/* Assigned To */}
          <div className="flex items-center gap-2 text-gray-400">
            <Users className="h-4 w-4 flex-shrink-0" />
            <span className="truncate">{getUserName(task.assigned_to)}</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Kanban Column Component
const KanbanColumn = ({ status, tasks, title, count, getProjectName, getUserName, getPriorityBadge, onTaskClick }) => {
  const { setNodeRef } = useSortable({ id: status });

  return (
    <div ref={setNodeRef} className="flex flex-col h-full min-w-[320px]">
      <div className="flex items-center justify-between mb-4 px-2">
        <h3 className="text-lg font-bold text-[#C9A961] flex items-center gap-2">
          {title}
          <Badge className="bg-[#C9A961]/20 text-[#C9A961] border-[#C9A961]/50">
            {count}
          </Badge>
        </h3>
      </div>
      <div className="flex-1 overflow-y-auto pr-2">
        <SortableContext items={tasks.map(t => t.id)} strategy={verticalListSortingStrategy}>
          {tasks.map((task) => (
            <SortableTaskCard
              key={task.id}
              task={task}
              getProjectName={getProjectName}
              getUserName={getUserName}
              getPriorityBadge={getPriorityBadge}
              onClick={(e) => {
                e.stopPropagation();
                onTaskClick(task);
              }}
            />
          ))}
        </SortableContext>
        {tasks.length === 0 && (
          <div className="text-center py-8 text-gray-500 text-sm">
            No tasks
          </div>
        )}
      </div>
    </div>
  );
};

const TasksPageKanban = () => {
  const [tasks, setTasks] = useState([]);
  const [projects, setProjects] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [selectedTask, setSelectedTask] = useState(null);
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('all'); // 'field', 'office', 'all'
  const [filterProject, setFilterProject] = useState('all');
  const [filterPriority, setFilterPriority] = useState('all');
  const [activeId, setActiveId] = useState(null);
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    location_type: 'field',
    is_project_task: true,
    project_id: '',
    department: '',
    assigned_to: [],
    status: 'todo',
    due_date: '',
    priority: 'medium',
    address: '',
    created_by: '',
  });

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );

  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
  const isEmployee = currentUser.role === 'employee';
  const canFullEdit = !isEmployee;

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [tasksRes, projectsRes, usersRes] = await Promise.all([
        api.get('/tasks'),
        api.get('/projects'),
        api.get('/users'),
      ]);
      setTasks(tasksRes.data);
      setProjects(projectsRes.data);
      setUsers(usersRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleDragStart = (event) => {
    setActiveId(event.active.id);
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    setActiveId(null);

    if (!over) return;

    const activeTask = tasks.find(t => t.id === active.id);
    const overStatus = over.id;

    // Check if we're dropping on a column or another task
    const isColumn = ['todo', 'in_progress', 'completed'].includes(overStatus);
    const newStatus = isColumn ? overStatus : tasks.find(t => t.id === overStatus)?.status;

    if (activeTask && newStatus && activeTask.status !== newStatus) {
      try {
        await api.put(`/tasks/${activeTask.id}`, { status: newStatus });
        toast.success('Task status updated');
        fetchData();
      } catch (error) {
        toast.error('Failed to update task status');
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (formData.is_project_task && !formData.project_id) {
      toast.error('Please select a project for project tasks');
      return;
    }
    if (formData.location_type === 'field' && !formData.address) {
      toast.error('Address is required for field tasks');
      return;
    }

    try {
      const data = { ...formData };
      if (data.due_date) {
        data.due_date = new Date(data.due_date).toISOString();
      }
      if (!data.is_project_task) {
        data.project_id = null;
      }

      if (!editingTask) {
        data.created_by = localStorage.getItem('username') || 'Admin';
      }

      if (editingTask) {
        await api.put(`/tasks/${editingTask.id}`, data);
        toast.success('Task updated successfully');
      } else {
        await api.post('/tasks', data);
        toast.success('Task created successfully');
      }
      fetchData();
      handleCloseDialog();
    } catch (error) {
      toast.error(formatErrorMessage(error, 'Operation failed'));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this task?')) return;
    try {
      await api.delete(`/tasks/${id}`);
      toast.success('Task deleted successfully');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete task');
    }
  };

  const handleEdit = (task) => {
    setEditingTask(task);
    setFormData({
      title: task.title || '',
      description: task.description || '',
      location_type: task.location_type || 'field',
      is_project_task: task.is_project_task !== undefined ? task.is_project_task : true,
      project_id: task.project_id || '',
      department: task.department || '',
      assigned_to: task.assigned_to || [],
      status: task.status || 'todo',
      due_date: task.due_date ? new Date(task.due_date).toISOString().split('T')[0] : '',
      priority: task.priority || 'medium',
      address: task.address || '',
      created_by: task.created_by || '',
    });
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingTask(null);
    setFormData({
      title: '',
      description: '',
      location_type: 'field',
      is_project_task: true,
      project_id: '',
      department: '',
      assigned_to: [],
      status: 'todo',
      due_date: '',
      priority: 'medium',
      address: '',
    });
  };

  const getPriorityBadge = (priority) => {
    const variants = {
      low: { label: 'Low', className: 'bg-[#C9A961]/20 text-[#C9A961] border border-[#C9A961]/50' },
      medium: { label: 'Medium', className: 'bg-yellow-500/20 text-yellow-300 border border-yellow-500/50' },
      high: { label: 'High', className: 'bg-red-500/20 text-red-300 border border-red-500/50' },
    };
    const variant = variants[priority] || variants.medium;
    return <Badge className={`${variant.className} text-xs`}>{variant.label}</Badge>;
  };

  const getProjectName = (projectId) => {
    const project = projects.find((p) => p.id === projectId);
    return project ? project.name : 'N/A';
  };

  const getUserName = (userIds) => {
    if (!Array.isArray(users)) return 'Unassigned';
    if (!userIds || userIds.length === 0) return 'Unassigned';
    
    const userIdArray = Array.isArray(userIds) ? userIds : [userIds];
    const userNames = userIdArray.map(userId => {
      const user = users.find((u) => u.id === userId);
      if (!user) return 'Unknown';
      if (user.first_name && user.last_name) {
        return `${user.first_name} ${user.last_name}`;
      }
      if (user.email && user.email.includes('@')) {
        const emailPart = user.email.split('@')[0];
        if (emailPart.includes('.')) {
          return emailPart.split('.').map(part => 
            part.charAt(0).toUpperCase() + part.slice(1)
          ).join(' ');
        }
      }
      return user.username.charAt(0).toUpperCase() + user.username.slice(1);
    });
    
    return userNames.join(', ');
  };

  // Filter tasks based on active tab and filters
  const filteredTasks = tasks.filter(task => {
    // Set default values for backward compatibility
    const taskLocationType = task.location_type || 'field';
    const taskIsProjectTask = task.is_project_task !== undefined ? task.is_project_task : true;
    
    // Tab filter
    if (activeTab === 'field' && taskLocationType !== 'field') return false;
    if (activeTab === 'office' && taskLocationType !== 'office') return false;
    
    // Project filter
    if (filterProject !== 'all') {
      if (filterProject === 'standalone' && taskIsProjectTask) return false;
      if (filterProject !== 'standalone' && task.project_id !== filterProject) return false;
    }
    
    // Priority filter
    if (filterPriority !== 'all' && task.priority !== filterPriority) return false;
    
    return true;
  });

  const todoTasks = filteredTasks.filter(t => t.status === 'todo');
  const inProgressTasks = filteredTasks.filter(t => t.status === 'in_progress');
  const completedTasks = filteredTasks.filter(t => t.status === 'completed');

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div style={{ color: ELEGANT_GOLD }}>Loading...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6 h-screen flex flex-col" data-testid="tasks-page">
      {/* Header Section */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-4xl font-bold mb-2 flex items-center gap-3 font-serif">
            <Calendar className="h-10 w-10 text-[#C9A961]" />
            <span className="text-white">Task</span>{' '}
            <span className="text-[#C9A961] italic">Management</span>
          </h1>
          <p className="text-gray-400 text-lg">
            Track and manage your tasks {isEmployee ? '(Update Status Only)' : ''}
          </p>
        </div>
        {canFullEdit && (
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button 
                onClick={() => setEditingTask(null)} 
                className="bg-[#C9A961] text-black hover:bg-[#D4C278] hover:scale-105 transition-all shadow-lg"
              >
                <Plus className="mr-2 h-5 w-5" /> Create Task
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl bg-dark-card border-[#C9A961]/30 max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="text-2xl text-[#C9A961] flex items-center gap-2">
                  <Calendar className="h-6 w-6" />
                  {editingTask ? 'Edit Task' : 'Create New Task'}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                {/* Task Location Type - Cleaner Buttons */}
                <div className="space-y-2">
                  <Label className="text-[#C9A961]">Task Location Type</Label>
                  <div className="grid grid-cols-2 gap-3">
                    <Button
                      type="button"
                      variant="outline"
                      className={`h-12 justify-start ${formData.location_type === 'field' ? 'bg-[#C9A961]/20 border-[#C9A961] text-[#C9A961]' : 'border-gray-600 text-gray-400'}`}
                      onClick={() => setFormData({ ...formData, location_type: 'field' })}
                    >
                      <MapPin className="mr-2 h-4 w-4" />
                      🏗️ Field Task
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      className={`h-12 justify-start ${formData.location_type === 'office' ? 'bg-[#C9A961]/20 border-[#C9A961] text-[#C9A961]' : 'border-gray-600 text-gray-400'}`}
                      onClick={() => setFormData({ ...formData, location_type: 'office' })}
                    >
                      <Building2 className="mr-2 h-4 w-4" />
                      🏢 Office Task
                    </Button>
                  </div>
                </div>

                {/* Project Association */}
                <div className="space-y-2">
                  <Label className="text-[#C9A961]">Project Association</Label>
                  <div className="grid grid-cols-2 gap-3">
                    <Button
                      type="button"
                      variant="outline"
                      className={`h-12 justify-start ${formData.is_project_task ? 'bg-[#C9A961]/20 border-[#C9A961] text-[#C9A961]' : 'border-gray-600 text-gray-400'}`}
                      onClick={() => setFormData({ ...formData, is_project_task: true, department: '' })}
                    >
                      <FolderKanban className="mr-2 h-4 w-4" />
                      Link to Project
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      className={`h-12 justify-start ${!formData.is_project_task ? 'bg-[#C9A961]/20 border-[#C9A961] text-[#C9A961]' : 'border-gray-600 text-gray-400'}`}
                      onClick={() => setFormData({ ...formData, is_project_task: false, project_id: '' })}
                    >
                      <Building2 className="mr-2 h-4 w-4" />
                      Standalone Task
                    </Button>
                  </div>
                </div>

                {/* Conditional Project Dropdown */}
                {formData.is_project_task && (
                  <div className="space-y-2">
                    <Label className="text-[#C9A961]">Select Project</Label>
                    <Select value={formData.project_id} onValueChange={(value) => setFormData({ ...formData, project_id: value })}>
                      <SelectTrigger className="bg-black/50 border-0 text-white focus:ring-2 focus:ring-[#C9A961]">
                        <SelectValue placeholder="Select a project" />
                      </SelectTrigger>
                      <SelectContent className="bg-dark-card border-[#C9A961]/30">
                        {projects.map((project) => (
                          <SelectItem key={project.id} value={project.id} className="text-white hover:bg-[#C9A961]/20">
                            {project.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {/* Conditional Department Dropdown */}
                {!formData.is_project_task && (
                  <div className="space-y-2">
                    <Label className="text-[#C9A961]">Department/Category (Optional)</Label>
                    <Select value={formData.department} onValueChange={(value) => setFormData({ ...formData, department: value })}>
                      <SelectTrigger className="bg-black/50 border-0 text-white focus:ring-2 focus:ring-[#C9A961]">
                        <SelectValue placeholder="Select department" />
                      </SelectTrigger>
                      <SelectContent className="bg-dark-card border-[#C9A961]/30">
                        <SelectItem value="" className="text-white hover:bg-[#C9A961]/20">None</SelectItem>
                        <SelectItem value="Human Resources" className="text-white hover:bg-[#C9A961]/20">Human Resources</SelectItem>
                        <SelectItem value="Accounting" className="text-white hover:bg-[#C9A961]/20">Accounting</SelectItem>
                        <SelectItem value="Administration" className="text-white hover:bg-[#C9A961]/20">Administration</SelectItem>
                        <SelectItem value="Maintenance" className="text-white hover:bg-[#C9A961]/20">Maintenance</SelectItem>
                        <SelectItem value="General" className="text-white hover:bg-[#C9A961]/20">General</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {/* Task Title */}
                <div className="space-y-2">
                  <Label className="text-[#C9A961]">Task Title</Label>
                  <Input
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    required
                    className="bg-black/50 border-0 text-white focus:ring-2 focus:ring-[#C9A961]"
                    placeholder="Enter task title"
                  />
                </div>

                {/* Description */}
                <div className="space-y-2">
                  <Label className="text-[#C9A961]">Description</Label>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="bg-black/50 border-0 text-white focus:ring-2 focus:ring-[#C9A961]"
                    placeholder="Task description"
                    rows={4}
                  />
                </div>

                {/* Address */}
                <div className="space-y-2">
                  <Label className="text-[#C9A961]">Address</Label>
                  <Input
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    required={formData.location_type === 'field'}
                    className="bg-black/50 border-0 text-white focus:ring-2 focus:ring-[#C9A961]"
                    placeholder={formData.location_type === 'field' ? 'Task location (required for field tasks)' : 'Task location (optional for office tasks)'}
                  />
                </div>

                {/* Assign Team Members */}
                <div className="space-y-2">
                  <Label className="text-[#C9A961]">Assign Team Members</Label>
                  <MultiSelectUsers
                    users={users}
                    selectedUsers={formData.assigned_to}
                    onSelectionChange={(selectedUserIds) => setFormData({ ...formData, assigned_to: selectedUserIds })}
                    placeholder="Select team members to assign"
                  />
                  <p className="text-xs text-gray-500">
                    📧 Selected users will receive email notifications automatically
                  </p>
                </div>

                {/* Status, Priority, Due Date - 2 Column Grid for Better Spacing */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-[#C9A961]">Status</Label>
                    <Select value={formData.status} onValueChange={(value) => setFormData({ ...formData, status: value })}>
                      <SelectTrigger className="bg-black/50 border-0 text-white focus:ring-2 focus:ring-[#C9A961]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-dark-card border-[#C9A961]/30">
                        <SelectItem value="todo" className="text-white hover:bg-[#C9A961]/20">To Do</SelectItem>
                        <SelectItem value="in_progress" className="text-white hover:bg-[#C9A961]/20">In Progress</SelectItem>
                        <SelectItem value="completed" className="text-white hover:bg-[#C9A961]/20">Completed</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-[#C9A961]">Priority</Label>
                    <Select value={formData.priority} onValueChange={(value) => setFormData({ ...formData, priority: value })}>
                      <SelectTrigger className="bg-black/50 border-0 text-white focus:ring-2 focus:ring-[#C9A961]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-dark-card border-[#C9A961]/30">
                        <SelectItem value="low" className="text-white hover:bg-[#C9A961]/20">Low</SelectItem>
                        <SelectItem value="medium" className="text-white hover:bg-[#C9A961]/20">Medium</SelectItem>
                        <SelectItem value="high" className="text-white hover:bg-[#C9A961]/20">High</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-[#C9A961]">Due Date</Label>
                  <Input
                    type="date"
                    value={formData.due_date}
                    onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                    className="bg-black/50 border-0 text-white focus:ring-2 focus:ring-[#C9A961]"
                  />
                </div>

                {/* Submit Buttons */}
                <div className="flex gap-3 pt-4">
                  <Button type="submit" className="flex-1 bg-[#C9A961] text-black hover:bg-[#D4C278] hover:scale-105 transition-all">
                    {editingTask ? 'Update Task' : 'Create Task'}
                  </Button>
                  <Button type="button" variant="outline" onClick={handleCloseDialog} className="border-[#C9A961] text-[#C9A961] hover:bg-[#C9A961]/10">
                    Cancel
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-4 border-b border-[#C9A961]/20 pb-2">
        <Button
          variant="ghost"
          className={`${activeTab === 'field' ? 'text-[#C9A961] border-b-2 border-[#C9A961]' : 'text-gray-400'} rounded-none`}
          onClick={() => setActiveTab('field')}
        >
          🏗️ Field Tasks ({tasks.filter(t => (t.location_type || 'field') === 'field').length})
        </Button>
        <Button
          variant="ghost"
          className={`${activeTab === 'office' ? 'text-[#C9A961] border-b-2 border-[#C9A961]' : 'text-gray-400'} rounded-none`}
          onClick={() => setActiveTab('office')}
        >
          🏢 Office Tasks ({tasks.filter(t => (t.location_type || 'field') === 'office').length})
        </Button>
        <Button
          variant="ghost"
          className={`${activeTab === 'all' ? 'text-[#C9A961] border-b-2 border-[#C9A961]' : 'text-gray-400'} rounded-none`}
          onClick={() => setActiveTab('all')}
        >
          All Tasks ({tasks.length})
        </Button>
      </div>

      {/* Filters */}
      <div className="flex gap-4">
        <Select value={filterProject} onValueChange={setFilterProject}>
          <SelectTrigger className="w-[200px] bg-black/50 border-[#C9A961]/30 text-white">
            <SelectValue placeholder="Filter by project" />
          </SelectTrigger>
          <SelectContent className="bg-dark-card border-[#C9A961]/30">
            <SelectItem value="all" className="text-white hover:bg-[#C9A961]/20">All Projects</SelectItem>
            <SelectItem value="standalone" className="text-white hover:bg-[#C9A961]/20">Standalone Only</SelectItem>
            {projects.map((project) => (
              <SelectItem key={project.id} value={project.id} className="text-white hover:bg-[#C9A961]/20">
                {project.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={filterPriority} onValueChange={setFilterPriority}>
          <SelectTrigger className="w-[200px] bg-black/50 border-[#C9A961]/30 text-white">
            <SelectValue placeholder="Filter by priority" />
          </SelectTrigger>
          <SelectContent className="bg-dark-card border-[#C9A961]/30">
            <SelectItem value="all" className="text-white hover:bg-[#C9A961]/20">All Priorities</SelectItem>
            <SelectItem value="high" className="text-white hover:bg-[#C9A961]/20">🔴 High</SelectItem>
            <SelectItem value="medium" className="text-white hover:bg-[#C9A961]/20">🟡 Medium</SelectItem>
            <SelectItem value="low" className="text-white hover:bg-[#C9A961]/20">🔵 Low</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Kanban Board */}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCorners}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        <div className="flex-1 overflow-hidden">
          <div className="flex gap-6 h-full overflow-x-auto pb-4">
            <SortableContext items={['todo', 'in_progress', 'completed']} strategy={verticalListSortingStrategy}>
              <KanbanColumn
                status="todo"
                title="To Do"
                count={todoTasks.length}
                tasks={todoTasks}
                getProjectName={getProjectName}
                getUserName={getUserName}
                getPriorityBadge={getPriorityBadge}
                onTaskClick={(task) => {
                  setSelectedTask(task);
                  setGalleryOpen(true);
                }}
              />
              <KanbanColumn
                status="in_progress"
                title="In Progress"
                count={inProgressTasks.length}
                tasks={inProgressTasks}
                getProjectName={getProjectName}
                getUserName={getUserName}
                getPriorityBadge={getPriorityBadge}
                onTaskClick={(task) => {
                  setSelectedTask(task);
                  setGalleryOpen(true);
                }}
              />
              <KanbanColumn
                status="completed"
                title="Completed"
                count={completedTasks.length}
                tasks={completedTasks}
                getProjectName={getProjectName}
                getUserName={getUserName}
                getPriorityBadge={getPriorityBadge}
                onTaskClick={(task) => {
                  setSelectedTask(task);
                  setGalleryOpen(true);
                }}
              />
            </SortableContext>
          </div>
        </div>

        <DragOverlay>
          {activeId ? (
            <Card className="bg-dark-card border-0 shadow-gold w-[320px] opacity-90">
              <CardContent className="p-4">
                <p className="text-white font-semibold">
                  {tasks.find(t => t.id === activeId)?.title}
                </p>
              </CardContent>
            </Card>
          ) : null}
        </DragOverlay>
      </DndContext>

      {/* Full Screen Task View */}
      {selectedTask && (
        <FileGalleryFullScreen
          isOpen={galleryOpen}
          onClose={() => {
            setGalleryOpen(false);
            setSelectedTask(null);
          }}
          record={selectedTask}
          recordType="tasks"
          files={selectedTask.files || []}
          onDelete={() => {}}
          canDelete={canFullEdit}
          onUpdate={fetchData}
          users={users}
        />
      )}
    </div>
  );
};

export default TasksPageKanban;
